import { KeyboardEvent, useEffect, useState } from "react";
import dayjs from "dayjs";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import IconButton from "@mui/material/IconButton";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import CloseIcon from "@mui/icons-material/Close";
import ImageNotSupportedOutlinedIcon from "@mui/icons-material/ImageNotSupportedOutlined";
import TextField from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import CheckIcon from "@mui/icons-material/Check";
import FullscreenIcon from "@mui/icons-material/Fullscreen";
import { SaleDepositSlip } from "../../types/sale-deposit.type";
import { updateSaleDepositSlipAmount } from "../../services";
import { formatNumber } from "../../utils";
import { COMPACT_QUERY } from "../../utils/hooks/useCompact";
import SlipFullScreenViewer from "./SlipFullScreenViewer";
import { isMoneyInput } from "./transfer.utils";

const compact = `@media ${COMPACT_QUERY}`;

interface TransferSlipsModalProps {
  open: boolean;
  slips: SaleDepositSlip[];
  onClose: () => void;
  /** The deposit these slips belong to. Required to correct an amount. */
  depositUuid?: string | null;
  /**
   * Whether the viewer's role carries finance/accounting rights over the figures. A deposit
   * inside a closed branch-day is refused by the server as well, but neither caller loads
   * that state, so it is not mirrored here.
   */
  canEditAmount?: boolean;
  /** Fired after a successful correction so the caller can refetch the row's totals. */
  onAmountUpdated?: () => void | Promise<void>;
}

const displayDate = (value: string | null) => {
  if (!value) return "-";
  const parsed = dayjs(value);
  return parsed.isValid() ? parsed.format("DD/MM/YYYY") : "-";
};

const displayTime = (value: string | null) =>
  value && value.trim() ? `${value} น.` : "-";

export default function TransferSlipsModal({
  open,
  slips,
  onClose,
  depositUuid = null,
  canEditAmount = false,
  onAmountUpdated,
}: TransferSlipsModalProps) {
  const [failedImages, setFailedImages] = useState<Record<string, boolean>>({});
  // uuid of the slip whose amount is being edited inline; null = nothing open.
  const [editingUuid, setEditingUuid] = useState<string | null>(null);
  const [draftAmount, setDraftAmount] = useState<string>("");
  const [saving, setSaving] = useState<boolean>(false);
  // Index of the slip being inspected full-screen; null = the viewer is closed.
  const [fullScreenIndex, setFullScreenIndex] = useState<number | null>(null);

  useEffect(() => {
    if (open) setFailedImages({});
  }, [open, slips]);

  // Closing the dialog must not leave a half-typed correction to reappear next time.
  useEffect(() => {
    if (!open) {
      setEditingUuid(null);
      setDraftAmount("");
      setFullScreenIndex(null);
    }
  }, [open]);

  // Curried so each row binds its own index/key without an inline arrow in JSX.
  const openFullScreen = (index: number) => () => setFullScreenIndex(index);

  const openFullScreenOnKey =
    (index: number) => (event: KeyboardEvent<HTMLElement>) => {
      if (event.key === "Enter" || event.key === " ") {
        event.preventDefault();
        setFullScreenIndex(index);
      }
    };

  const markImageFailed = (imageKey: string) => () =>
    setFailedImages((previous) => ({ ...previous, [imageKey]: true }));

  const closeFullScreen = () => setFullScreenIndex(null);

  const startEdit = (slip: SaleDepositSlip) => {
    setEditingUuid(slip.uuid);
    setDraftAmount(slip.transfer_amount == null ? "" : String(slip.transfer_amount));
  };

  const cancelEdit = () => {
    setEditingUuid(null);
    setDraftAmount("");
  };

  const saveEdit = async (slip: SaleDepositSlip) => {
    if (!depositUuid) return;
    const amount = Number(draftAmount);
    if (draftAmount.trim() === "" || !Number.isFinite(amount) || amount < 0) return;
    // No write for a no-op correction — it would stamp a misleading audit entry.
    if (amount === slip.transfer_amount) {
      cancelEdit();
      return;
    }

    setSaving(true);
    try {
      await updateSaleDepositSlipAmount(depositUuid, slip.uuid, amount);
      cancelEdit();
      await onAmountUpdated?.();
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      slotProps={{
        paper: {
          sx: {
            p: 0,
            maxHeight: "90vh",
            [compact]: { maxHeight: "94vh", m: 2 },
          },
        },
      }}
    >
      <DialogTitle sx={{ fontWeight: 700, pr: 6 }}>
        สลิปการโอน
        <IconButton
          aria-label="ปิด"
          onClick={onClose}
          sx={{ position: "absolute", right: 8, top: 8 }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ overflowY: "auto" }}>
        {slips.length === 0 ? (
          <Typography color="text.secondary" align="center" sx={{ py: 5 }}>
            ไม่มีสลิปการโอน
          </Typography>
        ) : (
          <Stack gap={3}>
            {slips.map((slip, index) => {
              const imageKey = `${slip.uuid}-${index}`;
              const imageUnavailable =
                !slip.slip_url || Boolean(failedImages[imageKey]);

              return (
                <Box
                  key={imageKey}
                  sx={{
                    border: "1px solid",
                    borderColor: "divider",
                    borderRadius: 2,
                    overflow: "hidden",
                  }}
                >
                  <Box sx={{ p: 2, bgcolor: "#f7f7fb" }}>
                    <Stack
                      direction="row"
                      alignItems="center"
                      justifyContent="space-between"
                      gap={1}
                    >
                      <Typography fontWeight={700}>
                        สลิป {slip.sequence || index + 1}
                      </Typography>
                      {!imageUnavailable && (
                        <Tooltip title="ดูเต็มหน้าจอ">
                          <IconButton
                            size="small"
                            color="primary"
                            aria-label={`ดูสลิป ${slip.sequence || index + 1} เต็มหน้าจอ`}
                            onClick={openFullScreen(index)}
                          >
                            <FullscreenIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Stack>
                    <Box
                      sx={{
                        display: "grid",
                        gridTemplateColumns: { xs: "1fr", sm: "repeat(3, 1fr)" },
                        gap: 1.5,
                        mt: 1,
                      }}
                    >
                      <Box>
                        <Typography variant="caption" color="text.secondary">
                          จำนวนเงินโอน
                        </Typography>
                        {editingUuid === slip.uuid ? (
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 0.5,
                              mt: 0.5,
                            }}
                          >
                            <TextField
                              size="small"
                              autoFocus
                              value={draftAmount}
                              onChange={(event) => {
                                const next = event.target.value;
                                if (isMoneyInput(next)) setDraftAmount(next);
                              }}
                              onKeyDown={(event) => {
                                if (event.key === "Enter") void saveEdit(slip);
                                if (event.key === "Escape") cancelEdit();
                              }}
                              disabled={saving}
                              slotProps={{
                                htmlInput: {
                                  inputMode: "decimal",
                                  "aria-label": `แก้ไขจำนวนเงินโอน สลิป ${slip.sequence || index + 1}`,
                                },
                              }}
                              sx={{ maxWidth: 130 }}
                            />
                            <IconButton
                              size="small"
                              color="primary"
                              aria-label="บันทึกจำนวนเงินโอน"
                              onClick={() => void saveEdit(slip)}
                              disabled={saving}
                            >
                              <CheckIcon fontSize="small" />
                            </IconButton>
                            <IconButton
                              size="small"
                              aria-label="ยกเลิกการแก้ไข"
                              onClick={cancelEdit}
                              disabled={saving}
                            >
                              <CloseIcon fontSize="small" />
                            </IconButton>
                          </Box>
                        ) : (
                          <Box
                            sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
                          >
                            <Typography variant="body2" fontWeight={600}>
                              {formatNumber(slip.transfer_amount)} บาท
                            </Typography>
                            {canEditAmount && depositUuid && (
                              <Tooltip title="แก้ไขจำนวนเงินโอน">
                                <IconButton
                                  size="small"
                                  aria-label={`แก้ไขจำนวนเงินโอน สลิป ${slip.sequence || index + 1}`}
                                  onClick={() => startEdit(slip)}
                                >
                                  <EditOutlinedIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            )}
                          </Box>
                        )}
                        {/* Only present once corrected — so this line IS the signal that the
                            figure on screen is no longer the salesperson's own. */}
                        {slip.original_transfer_amount != null && (
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ display: "block" }}
                          >
                            พนักงานกรอก {formatNumber(slip.original_transfer_amount)}
                            {slip.corrected_by ? ` · แก้โดย ${slip.corrected_by}` : ""}
                          </Typography>
                        )}
                      </Box>
                      <Box>
                        <Typography variant="caption" color="text.secondary">
                          วันที่โอน
                        </Typography>
                        <Typography variant="body2">
                          {displayDate(slip.transfer_date)}
                        </Typography>
                      </Box>
                      <Box>
                        <Typography variant="caption" color="text.secondary">
                          เวลาที่โอน
                        </Typography>
                        <Typography variant="body2">
                          {displayTime(slip.transfer_time)}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>

                  {imageUnavailable ? (
                    <Box
                      role="img"
                      aria-label={`ไม่มีรูปสลิป ${slip.sequence || index + 1}`}
                      sx={{
                        minHeight: 220,
                        p: 3,
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: 1,
                        bgcolor: "background.default",
                        color: "text.secondary",
                      }}
                    >
                      <ImageNotSupportedOutlinedIcon sx={{ fontSize: 44 }} />
                      <Typography variant="body2">
                        {slip.slip_url
                          ? "ไม่สามารถแสดงรูปสลิปได้"
                          : "ไม่มีรูปสลิป"}
                      </Typography>
                    </Box>
                  ) : (
                    <Box
                      component="img"
                      src={slip.slip_url ?? undefined}
                      alt={`สลิป ${slip.sequence || index + 1}`}
                      role="button"
                      tabIndex={0}
                      title="คลิกเพื่อดูเต็มหน้าจอ"
                      onClick={openFullScreen(index)}
                      onKeyDown={openFullScreenOnKey(index)}
                      onError={markImageFailed(imageKey)}
                      sx={{
                        display: "block",
                        width: "100%",
                        maxHeight: 520,
                        objectFit: "contain",
                        bgcolor: "background.default",
                        cursor: "zoom-in",
                      }}
                    />
                  )}
                </Box>
              );
            })}
          </Stack>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button variant="contained" onClick={onClose}>
          ปิด
        </Button>
      </DialogActions>

      <SlipFullScreenViewer
        index={fullScreenIndex}
        slips={slips}
        onChangeIndex={setFullScreenIndex}
        onClose={closeFullScreen}
      />
    </Dialog>
  );
}
